/**
 * STABLE VERSION - Desktop and Tablet
 * 
 * This is a stable version of BagBuilder component with working:
 * - Desktop initials-tag positioning with inline styles
 * - Tablet initials-tag positioning with inline styles (scale 1.1, margins: 19px/-315px)
 * - Dynamic positioning logic for both desktop and tablet versions
 * - Final inline styles applied on stage 2 for both versions
 */
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { gsap } from 'gsap';
import './BagBuilder.css';
import productsJson from '../../data/Products.json';
import { getProductSku } from '../../utils/productsStorage';
// Removed loadProducts import - using productsJson directly
import { useTranslation } from 'react-i18next';
import goldleafImage from '../../assets/main_assets/goldleaf.jpg';
import brownleafImage from '../../assets/main_assets/brownleaf.webp';
import arrowIcon from '../../assets/arrow.svg';
import { trackUpdateProduct, trackUpdateLetters, trackFinalizeSelection, trackClosingCTA, trackModalView, trackClick, trackPageView, updateDataLayer } from '../../analytics';

// Webpack (CRA) friendly resolver using require.context
// Use recursive require.context to access all subdirectories in Bags
// Support both .jpg and .tif files
const bagsCtxJpg = require.context('../../assets/main_assets/Bags', true, /\.jpg$/i);
const bagsCtxTif = require.context('../../assets/main_assets/Bags', true, /\.tif$/i);
const newBagsCtxJpg = require.context('../../assets/new_bags_march2026', true, /\.jpg$/i);

// Cache for keys to avoid repeated lookups
const jpgKeys = bagsCtxJpg.keys();
const tifKeys = bagsCtxTif.keys();
const newBagsKeys = newBagsCtxJpg.keys();

const resolveGallery = (p) => {
  // If it's already a base64 data URL, return it as is
  if (p && p.startsWith('data:')) {
    return p;
  }
  if (!p || p === '') {
    return p; // Return empty string as is
  }

  // Handle new_bags_march2026 assets
  if (p.startsWith('assets/new_bags_march2026/')) {
    const relativePath = p.replace(/^assets\/new_bags_march2026\//, '');
    const key = `./${relativePath}`;
    try {
      return newBagsCtxJpg(key);
    } catch (e) {
      // Fallback: search by filename
      const fileName = relativePath.split('/').pop().toLowerCase();
      const matchingKey = newBagsKeys.find(k => k.split('/').pop().toLowerCase() === fileName);
      if (matchingKey) {
        try { return newBagsCtxJpg(matchingKey); } catch (e2) {}
      }
      console.warn('Failed to resolve new bag image:', p);
      return '';
    }
  }

  // Extract the path relative to Bags folder
  // p format: "assets/bags_v1/01__8W2B0T31ABM0NO_NERO/8W2B0T31ABM0NO_SF.jpg" or "assets/main_assets/Bags/..."
  // We need: "01__8W2B0T31ABM0NO_NERO/8W2B0T31ABM0NO_SF.jpg"
  let relativePath = p.replace(/^assets\/bags_v1\//, '').replace(/^assets\/main_assets\/Bags\//, '');
  const fileName = relativePath.split('/').pop(); // Get just the filename

  // First, try to find by filename (case-insensitive) - this handles spaces and case issues
  const allKeys = [...jpgKeys, ...tifKeys];
  const matchingKey = allKeys.find(k => {
    const keyFileName = k.split('/').pop().toLowerCase();
    return keyFileName === fileName.toLowerCase();
  });

  if (matchingKey) {
    try {
      if (matchingKey.toLowerCase().endsWith('.jpg')) {
        const resolved = bagsCtxJpg(matchingKey);
        return resolved;
      } else if (matchingKey.toLowerCase().endsWith('.tif')) {
        const resolved = bagsCtxTif(matchingKey);
        return resolved;
      }
    } catch (e) {
      console.warn('Failed to load matched image:', matchingKey, 'for path:', p, e);
    }
  }

  // Fallback: try direct path
  const key = `./${relativePath}`;
  try {
    if (p.toLowerCase().endsWith('.jpg')) {
      const resolved = bagsCtxJpg(key);
      return resolved;
    } else if (p.toLowerCase().endsWith('.tif')) {
      const resolved = bagsCtxTif(key);
      return resolved;
    }
  } catch (e) {
    // Silent fail, will try next method
  }

  // Last resort: try both contexts
  try {
    const resolved = bagsCtxJpg(key);
    return resolved;
  } catch {
    try {
      const resolved = bagsCtxTif(key);
      return resolved;
    } catch (e) {
      console.warn('Failed to resolve image:', p, 'tried key:', key, 'matchingKey:', matchingKey);
      // Return empty string to prevent browser from fetching from server
      return '';
    }
  }
};

// Find the best front-facing image for the configurator.
// For old bags (Thumb modelli precedenti): resolve [SKU]_Sf.jpg directly from main_assets/Bags.
// For new bags: find the -0.jpg slide.
const findConfiguratorImage = (imagePath, slides) => {
  const isOldBag = imagePath && imagePath.includes('Thumb modelli precedenti');
  if (isOldBag) {
    // Extract SKU from thumb filename: "8W2B0T31MIRQT3-0-thumb.jpg" → "8W2B0T31MIRQT3"
    const sku = imagePath.split('/').pop().replace(/-0-thumb\.jpg$/i, '');
    if (sku) {
      // resolveGallery does case-insensitive filename matching across main_assets/Bags
      const sfImage = resolveGallery(`assets/main_assets/Bags/${sku}_Sf.jpg`);
      if (sfImage && sfImage !== '') return sfImage;
    }
  }
  // For new bags and fallback: find front slide by priority
  if (slides && slides.length > 0) {
    const patterns = [
      s => /-0\.jpg$/i.test(s),           // new bags: SKU-0.jpg
      s => /_SF\.jpg$/i.test(s),          // still front
      s => /ecommerce-1\.jpg$/i.test(s),  // ecommerce hero
      s => /intero\.jpg$/i.test(s),       // Italian front
      s => /intera\.jpg$/i.test(s),       // Italian front
    ];
    for (const match of patterns) {
      const found = slides.find(match);
      if (found) return resolveGallery(found);
    }
  }
  return null;
};

// Initial products from JSON - always use JSON directly, ignore localStorage
const initialProducts = productsJson.map(p => {
  const sku = getProductSku(p); // extract SKU from original path before resolveGallery overwrites image
  const thumbImage = resolveGallery(p.image);
  const slideImage = findConfiguratorImage(p.image, p.slides);
  return {
    ...p,
    sku,
    image: thumbImage,
    configuratorImage: slideImage || thumbImage,
  };
});

const BagBuilder = ({ initialInitials, initialProductIdx = null }) => {
  const { t } = useTranslation();
  const [products, setProducts] = useState(initialProducts);
  const [currentInitials, setCurrentInitials] = useState(initialInitials || 'ABC');
  const [format, setFormat] = useState('no-dots');
  // Use initialProductIdx if provided, otherwise default to 0
  const initialIdx = initialProductIdx !== null && initialProductIdx >= 0 && initialProductIdx < initialProducts.length 
    ? initialProductIdx 
    : 0;
  const [mainImage, setMainImage] = useState(initialProducts[initialIdx]?.configuratorImage || initialProducts[initialIdx]?.image || '/bag.png');
  const [selectedProductIdx, setSelectedProductIdx] = useState(initialIdx);
  const [showEditModal, setShowEditModal] = useState(false);
  const [stage, setStage] = useState(1); // 1: builder, 2: final
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(() => {
    const width = window.innerWidth;
    return width >= 768 && width <= 1366;
  });
  const [isTabletLandscape, setIsTabletLandscape] = useState(() => {
    const width = window.innerWidth;
    return (width >= 768 && width <= 1366) && window.innerHeight < width;
  });
  const [panelDragY, setPanelDragY] = useState(0);
  const thumbnailsRef = useRef(null);
  const controlsPanelRef = useRef(null);
  const imagePreviewRef = useRef(null);
  const productImageRef = useRef(null);
  const initialsTagRef = useRef(null);
  const finalContentRef = useRef(null);
  const mainContainerRef = useRef(null);
  const productsSectionRef = useRef(null);
  const homeIndicatorRef = useRef(null);
  const imagePreviewPosBeforeStageChange = useRef(null);
  const wrapperRef = useRef(null);
  // Global flag to prevent ANY tag position updates after animation completes
  const tagPositionLockedRef = useRef(false);
  const imagePosBeforeStageChange = useRef(null);
  const initialsTagPosBeforeStageChange = useRef(null);
  const lastThumbClickRef = useRef({ idx: null, timestamp: 0 });
  const thumbnailScrollingRef = useRef(false);
  // Store tag position percentages relative to bag image (computed once in handleFirstConfirm)
  const tagPositionPercentRef = useRef({ x: null, y: null, naturalOffsetX: null, naturalOffsetY: null });
  const noDots = useMemo(() => currentInitials.replace(/\./g, ''), [currentInitials]);
  const withDots = useMemo(() => noDots.split('').join('.') + '.', [noDots]);

  const displayInitials = format === 'no-dots' ? noDots : withDots;

  // Parallax scroll handler for controls-panel on mobile and tablet
  // When scrolling down, panel moves up to cover image
  // When scrolling up, panel moves down to reveal image
  const handleScroll = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const isMobileDevice = viewportWidth <= 767;
    const isTabletDevice = viewportWidth >= 768 && viewportWidth <= 1366;
    
    if (!isMobileDevice || isTabletDevice || !controlsPanelRef.current || !wrapperRef.current || stage !== 1) return;
    
    const wrapper = wrapperRef.current;
    const scrollTop = wrapper.scrollTop;
    const maxScroll = wrapper.scrollHeight - wrapper.clientHeight;
    
    // Prevent division by zero
    if (maxScroll <= 0) {
      setPanelDragY(0);
      if (controlsPanelRef.current) {
        controlsPanelRef.current.style.transform = 'translateY(0px)';
      }
      return;
    }
    
    // Calculate parallax offset: scroll down = panel moves up (negative translateY)
    // Maximum panel movement: 60% of viewport height for mobile, 30% for tablets
    // On tablets, limit movement to prevent panel from going beyond 50% of screen width
    const maxPanelMovement = isTabletDevice 
      ? Math.min(window.innerHeight * 0.3, window.innerWidth * 0.25) 
      : window.innerHeight * 0.6;
    
    // Calculate translateY based on scroll position
    // When scrollTop = 0: translateY = 0 (panel at bottom)
    // When scrollTop = maxScroll: translateY = -maxPanelMovement (panel covers image)
    // Use smooth easing for better UX
    const scrollProgress = Math.min(scrollTop / maxScroll, 1);
    const translateY = -scrollProgress * maxPanelMovement;
    
    setPanelDragY(translateY);
    if (controlsPanelRef.current) {
      controlsPanelRef.current.style.transform = `translateY(${translateY}px)`;
      // Remove transition during scroll for smooth real-time movement
      controlsPanelRef.current.style.transition = 'none';
    }
  }, [stage]);

  // Track mobile and tablet viewport size and recalculate parallax on resize
  useEffect(() => {
    const applyLandscapeTabletClass = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isLT = vw >= 1024 && vw <= 1366 && vw > vh;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;

      // Remove all model-specific classes first
      wrapper.classList.remove('bb-landscape-tablet', 'bb-ipad-mini', 'bb-ipad-air', 'bb-ipad-pro');

      if (isLT) {
        // Common landscape layout class
        wrapper.classList.add('bb-landscape-tablet');

        // Model-specific class based on viewport width
        if (vw <= 1100) {
          wrapper.classList.add('bb-ipad-mini'); // iPad / iPad Mini (1024px)
        } else if (vw <= 1280) {
          wrapper.classList.add('bb-ipad-air');  // iPad Air (1180px)
        } else {
          wrapper.classList.add('bb-ipad-pro');  // iPad Pro (1366px)
        }
      }
    };

    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      setIsMobile(viewportWidth <= 767);
      setIsTablet(viewportWidth >= 768 && viewportWidth <= 1366);
      setIsTabletLandscape((viewportWidth >= 768 && viewportWidth <= 1366) && viewportHeight < viewportWidth);
      applyLandscapeTabletClass();
      // Recalculate parallax position after resize for mobile and tablet
      if ((viewportWidth <= 767 || (viewportWidth >= 768 && viewportWidth <= 1366)) && stage === 1) {
        setTimeout(() => handleScroll(), 100);
      }
    };

    // Apply on mount
    applyLandscapeTabletClass();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [stage, handleScroll]);

  // Handle scroll to products section for mobile
  const handleExploreOtherStyles = () => {
    if (isMobile && productsSectionRef.current) {
      trackClick('bag_builder', 'explore_other_styles_mobile');
      productsSectionRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleThumb = (idx) => {
    // Prevent double call - check if same index was clicked recently (within 100ms)
    const now = Date.now();
    if (lastThumbClickRef.current.idx === idx && (now - lastThumbClickRef.current.timestamp) < 100) {
      return;
    }
    
    // Prevent double call if same product is already selected
    if (selectedProductIdx === idx) return;
    
    if (products[idx]) {
      const product = products[idx];
      lastThumbClickRef.current = { idx, timestamp: now };
      const updateSku = getProductSku(product);
      console.log('%c[ANALYTICS] update_product', 'color: cyan; font-weight: bold', { sku: updateSku, product });
      trackUpdateProduct(updateSku);
      setMainImage(products[idx].configuratorImage || products[idx].image);
      setSelectedProductIdx(idx);
    }
  };
  // Re-fire the bag-builder virtual_page_load (same payload as the mount effect).
  // Used when the Edit Initials modal closes, so the "landing back" page view is
  // tracked after the modal view.
  const trackBagBuilderPageView = useCallback(() => {
    const sku = products[selectedProductIdx] ? getProductSku(products[selectedProductIdx]) : '';
    updateDataLayer({ page_type: 'bag_builder', pagename: `/bag-builder/${sku}`, sku });
    trackPageView({ page_type: 'bag_builder', pagename: `/bag-builder/${sku}`, sku });
  }, [products, selectedProductIdx]);

  const handleConfirmNewLabel = (value) => {
    const v = (value || '').toUpperCase().replace(/[^A-Z]/g, '');
    if (!v) return;
    trackUpdateLetters(v);
    setCurrentInitials(v);
    setShowEditModal(false);
    trackBagBuilderPageView();
  };

  const scrollThumbnails = (direction) => {
    if (!thumbnailsRef.current) return;
    trackClick('bag_builder', `thumbnail_nav_${direction}`);
    const scrollAmount = 200;
    thumbnailsRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };

  const handleFirstConfirm = () => {
    const productImage = productImageRef.current;
    const imagePreview = imagePreviewRef.current;
    const tagElement = initialsTagRef.current;

    if (!productImage || !imagePreview) return;
    
    const product = products[selectedProductIdx];
    const ctaName = t('THATS_MY_DEVAIN') || 'THATS_MY_DEVAIN';
    const finalizeSku = product ? getProductSku(product) : ctaName;
    console.log('%c[ANALYTICS] finalize_selection', 'color: cyan; font-weight: bold', { sku: finalizeSku, initials: currentInitials, product });
    trackFinalizeSelection(
      finalizeSku,
      currentInitials || ''
    );
    
    // Hide controls panel immediately (no animation)
    const controlsPanel = controlsPanelRef.current;
    if (controlsPanel) {
      controlsPanel.style.display = 'none';
    }

    const viewportWidth = window.innerWidth;
    const viewportHeightCheck = window.innerHeight;
    const isTablet = viewportWidth >= 768 && viewportWidth <= 1366;
    const isLandscapeTablet = isTablet && viewportWidth > viewportHeightCheck && viewportWidth >= 1024 && viewportWidth <= 1366;

    // LANDSCAPE TABLETS: bag stays in stage 1 position — only swap the right panel
    if (isLandscapeTablet) {
      imagePreview.classList.add('image-preview--final');
      setStage(2);
      return;
    }

    // MOBILE: skip GSAP animation, show bag statically via CSS
    if (viewportWidth <= 767) {
      imagePreview.classList.add('image-preview--final');
      // Clear any inline transforms so CSS rules take over
      productImage.style.removeProperty('transform');
      productImage.style.removeProperty('transform-origin');
      productImage.style.removeProperty('opacity');
      setStage(2);
      return;
    }

    // DESKTOP: skip GSAP animation, bag stays in place
    if (viewportWidth > 1366) {
      imagePreview.classList.add('image-preview--final');
      productImage.style.removeProperty('transform');
      productImage.style.removeProperty('transform-origin');
      productImage.style.removeProperty('opacity');
      setStage(2);
      return;
    }

    // Below runs for non-landscape-tablet devices (portrait tablets, desktop)
    // Check iPad models — must match CSS class detection thresholds
    // iPad / iPad Mini landscape: 1024–1100px (bb-ipad-mini)
    const isIpadMini = viewportWidth >= 1024 && viewportWidth <= 1100;

    // iPad Air landscape: 1101–1280px (bb-ipad-air)
    const isIpadAir = viewportWidth >= 1101 && viewportWidth <= 1280;

    // iPad Pro landscape: 1281–1366px (bb-ipad-pro)
    const isIpadPro = viewportWidth >= 1281 && viewportWidth <= 1366;
    
    // Get initial values
    let initialBagX = parseFloat(gsap.getProperty(productImage, 'x'));
    let initialBagY = parseFloat(gsap.getProperty(productImage, 'y'));
    let initialScale = parseFloat(gsap.getProperty(productImage, 'scale'));
    
    // For tablet, use correct initial values
    if (isTablet) {
      // Force reflow to ensure CSS is applied
      void productImage.offsetHeight;
      
      // For tablets, use correct initial transform values: translate3d(-584px, -191px, 0px) scale(1.5, 1.5)
      initialScale = 1.5;
      initialBagX = -584;
      initialBagY = -191;
    } else {
      // Desktop defaults - use CSS values from BagBuilder.css
      // CSS: transform: translateX(-470px) translateY(-195px); scale: 2;
      initialBagX = -470;
      initialBagY = -195;
      initialScale = 2;
    }
    
    // Set target values based on viewport
    // Separate rules for each iPad model
    const viewportHeight = window.innerHeight;
    const isPortrait = viewportHeight > viewportWidth;
    const isIpadMiniLandscape = isTablet && isIpadMini && !isPortrait;
    const isIpadAirLandscape = isTablet && isIpadAir && !isPortrait;
    const isIpadProLandscape = isTablet && isIpadPro && !isPortrait;
    
    // Separate target values for each iPad model
    let targetScale, targetX, targetY, targetZ;
    if (isIpadMiniLandscape) {
      // iPad / iPad Mini landscape (1024–1100px)
      targetScale = 0.8;
      targetX = -2;
      targetY = 41;
      targetZ = 4;
    } else if (isIpadAirLandscape) {
      // iPad Air landscape (1101–1280px, 820px tall)
      targetScale = 0.8;
      targetX = 0;
      targetY = 41;
      targetZ = 4;
    } else if (isIpadProLandscape) {
      // iPad Pro landscape (1281–1366px, 1024px tall)
      targetScale = 0.8;
      targetX = 0;
      targetY = 41;
      targetZ = 4;
    } else if (isTablet) {
      // Fallback for any other tablet
      targetScale = 0.8;
      targetX = -2;
      targetY = 41;
      targetZ = 4;
    } else {
      // Desktop
      targetScale = 2.3;
      targetX = 0;
      targetY = 0;
      targetZ = 0;
    }
    
    // Apply CSS class for final state
    imagePreview.classList.add('image-preview--final');
    
    // Set initial state: current position, initial scale
    // For tablet: keep image visible (opacity: 1) to animate from visible state like desktop
    // For desktop: start invisible for fade-in
    const initialTransformOrigin = isTablet ? 'center center' : 'left';
    gsap.set(productImage, {
      x: initialBagX,
      y: initialBagY,
      scale: initialScale,
      transformOrigin: initialTransformOrigin,
      opacity: isTablet ? 1 : 0,
      force3D: true
    });

    productImage.style.setProperty('transform-origin', initialTransformOrigin, 'important');
    productImage.style.setProperty('transform', `translate3d(${initialBagX}px, ${initialBagY}px, 0px) scale(${initialScale}, ${initialScale})`, 'important');

    // Create timeline for fade-in and movement animation
    const tl = gsap.timeline();

    const animationProps = {
      opacity: 1,
      transformOrigin: 'center center',
      duration: 1.5,
      ease: 'power2.inOut',
      force3D: true,
      onStart: () => {
        productImage.style.setProperty('transform-origin', 'center center', 'important');
      },
      onComplete: () => {
        const currentViewportWidth = window.innerWidth;
        const currentViewportHeight = window.innerHeight;
        const currentIsTablet = currentViewportWidth >= 768 && currentViewportWidth <= 1366;

        if (currentIsTablet) {
          const isPortrait = currentViewportHeight > currentViewportWidth;
          // Match CSS class detection thresholds
          const currentIsIpadMini = currentViewportWidth >= 1024 && currentViewportWidth <= 1100;
          const currentIsIpadAir = currentViewportWidth >= 1101 && currentViewportWidth <= 1280;
          const currentIsIpadPro = currentViewportWidth >= 1281 && currentViewportWidth <= 1366;
          const isIpadMiniLandscape = currentIsTablet && currentIsIpadMini && !isPortrait;
          const isIpadAirLandscape = currentIsTablet && currentIsIpadAir && !isPortrait;
          const isIpadProLandscape = currentIsTablet && currentIsIpadPro && !isPortrait;

          // All landscape iPad models use the same final transform (targetX/Y/Z/Scale already set correctly above)
          gsap.set(productImage, {
            opacity: 1,
            x: targetX,
            y: targetY,
            z: targetZ,
            scale: targetScale,
            transformOrigin: 'center center',
            force3D: true
          });
          productImage.style.setProperty('transform', `translate3d(${targetX}px, ${targetY}px, ${targetZ}px) scale(${targetScale}, ${targetScale})`, 'important');
          productImage.style.setProperty('transform-origin', 'center center', 'important');
          productImage.style.setProperty('will-change', 'transform', 'important');
        } else {
          gsap.set(productImage, {
            opacity: 1,
            x: targetX,
            y: targetY,
            scale: targetScale,
            transformOrigin: 'center center',
            force3D: true
          });
          productImage.style.setProperty('transform', `translate3d(${targetX}px, ${targetY}px, 0px) scale(${targetScale}, ${targetScale})`, 'important');
          productImage.style.setProperty('transform-origin', 'center center', 'important');
          productImage.style.setProperty('will-change', 'transform', 'important');
        }

        setStage(2);
      }
    };

    if (isTablet) {
      animationProps.x = targetX;
      animationProps.y = targetY;
      animationProps.z = targetZ;
      animationProps.scale = targetScale;
    } else {
      animationProps.x = targetX;
      animationProps.y = targetY;
      animationProps.scale = targetScale;
    }

    tl.to(productImage, animationProps);
    
    if (false && tagElement) { // tag positioning handled by CSS
      // Get current tag opacity
      const computedStyle = window.getComputedStyle(tagElement);
      const initialsCurrentOpacity = parseFloat(computedStyle.opacity) || 0.9;
      
      // Get current visual positions (before GSAP transforms)
      // Force multiple reflows for tablets to ensure CSS is fully applied and layout is stable
      void productImage.offsetHeight;
      void tagElement.offsetHeight;
      if (isTablet) {
        // Additional reflow for tablets to ensure CSS transforms are calculated
        void productImage.offsetHeight;
        void tagElement.offsetHeight;
      }
      
      // First, position tag at the correct location on the black tag (659px, 604px relative to image-preview)
      // This ensures we calculate the correct relative position from the start
      const imagePreview = productImage.parentElement;
      const previewRect = imagePreview.getBoundingClientRect();
      
      // For tablets, adjust coordinates to position tag on the small black tag (not on main flap)
      let targetTagX = 659; // Correct position on black tag
      let targetTagY = 604; // Correct position on black tag
      
      if (isTablet) {
        // For tablets, the tag needs to be positioned higher and more to the right
        // to be on the small black tag attached to strap, not on main flap
        // Adjust based on image-preview scale (1.1) and transform-origin (-60% 280%)
        const scale = 1.1;
        const originXPercent = -60;
        const originYPercent = 280;
        
        // Calculate origin in pixels
        const originX = (originXPercent / 100) * previewRect.width;
        const originY = (originYPercent / 100) * previewRect.height;
        
        // Adjust coordinates: move tag higher (decrease Y) and more to the right (increase X)
        // Move 8px to the right and 44px up dynamically
        targetTagX = originX + (targetTagX - originX) / scale + 8; // Move 8px to the right
        targetTagY = originY + (targetTagY - originY) / scale - 44; // Move 44px up
      }
      
      // Set initial position to target position (temporary, will be replaced by GSAP)
      tagElement.style.position = 'absolute';
      tagElement.style.left = `${targetTagX}px`;
      tagElement.style.top = `${targetTagY}px`;
      tagElement.style.transform = 'translate(-50%, -50%) rotate(74deg)';
      tagElement.style.zIndex = '2';
      tagElement.style.pointerEvents = 'none';
      tagElement.style.marginTop = '0';
      tagElement.style.marginRight = '0';
      
      // Force reflow to ensure position is applied
      void tagElement.offsetHeight;
      void productImage.offsetHeight;
      
      // Now get the actual positions after setting target position
      const tagRect = tagElement.getBoundingClientRect();
      const bagRect = productImage.getBoundingClientRect();
      
      // Calculate tag's offset from bag's center (in screen pixels)
      const tagCenterX = tagRect.left + tagRect.width / 2;
      const tagCenterY = tagRect.top + tagRect.height / 2;
      const bagCenterX = bagRect.left + bagRect.width / 2;
      const bagCenterY = bagRect.top + bagRect.height / 2;
      
      // Offset from bag's center to tag's center (screen pixels at current scale)
      const screenOffsetX = tagCenterX - bagCenterX;
      const screenOffsetY = tagCenterY - bagCenterY;
      
      // Convert to natural (unscaled) offset - this is the RELATIVE position we want to maintain
      // Use actual initialScale for accurate calculation
      const effectiveInitialScale = initialScale || 1;
      const naturalOffsetX = screenOffsetX / effectiveInitialScale;
      const naturalOffsetY = screenOffsetY / effectiveInitialScale;
      
      // Calculate tag position as percentage of bag image dimensions (relative to top-left corner)
      // Get bag's natural dimensions (unscaled)
      const bagNaturalWidth = bagRect.width / effectiveInitialScale;
      const bagNaturalHeight = bagRect.height / effectiveInitialScale;
      
      // Calculate tag position relative to bag's top-left corner
      // Tag center = bag center + naturalOffset
      // Tag top-left = bag center + naturalOffset - tag half-size
      // But we need position relative to bag's top-left, so:
      // tagPercentX = (bagNaturalWidth/2 + naturalOffsetX) / bagNaturalWidth * 100
      // tagPercentY = (bagNaturalHeight/2 + naturalOffsetY) / bagNaturalHeight * 100
      const tagPercentX = ((bagNaturalWidth / 2 + naturalOffsetX) / bagNaturalWidth) * 100;
      const tagPercentY = ((bagNaturalHeight / 2 + naturalOffsetY) / bagNaturalHeight) * 100;
      
      // Store percentages and naturalOffset for use in updateInitialsTagPosition
      tagPositionPercentRef.current = {
        x: tagPercentX,
        y: tagPercentY,
        naturalOffsetX: naturalOffsetX,
        naturalOffsetY: naturalOffsetY
      };
      
      // Tag's constant scale - 1.1 for tablets, 1.3 for desktop
      const tagScale = isTablet ? 1.1 : 1.3;
      
      // Console log for debugging tag position calculation
      console.log('[initials-tag] GSAP sync positioning:', {
        stage: 'before animation',
        isTablet,
        initialScale,
        effectiveInitialScale,
        bagRect: {
          left: bagRect.left,
          top: bagRect.top,
          width: bagRect.width,
          height: bagRect.height,
          centerX: bagCenterX,
          centerY: bagCenterY
        },
        tagRect: {
          left: tagRect.left,
          top: tagRect.top,
          width: tagRect.width,
          height: tagRect.height,
          centerX: tagCenterX,
          centerY: tagCenterY
        },
        screenOffset: {
          x: screenOffsetX,
          y: screenOffsetY
        },
        naturalOffset: {
          x: naturalOffsetX,
          y: naturalOffsetY
        },
        tagScale
      });
      
      
      // Clear CSS margins - we'll use GSAP transforms only for all versions
      tagElement.style.marginTop = '0';
      tagElement.style.marginRight = '0';
      
      // Function to update tag position based on bag's current position
      // Uses percentage-based positioning relative to product-image dimensions
      const updateTagPosition = () => {
        // Force reflow for accurate measurements
        void productImage.offsetHeight;
        void tagElement.offsetHeight;
        
        // Get bag's current GSAP transforms
        const bagX = parseFloat(gsap.getProperty(productImage, 'x')) || 0;
        const bagY = parseFloat(gsap.getProperty(productImage, 'y')) || 0;
        const bagScale = parseFloat(gsap.getProperty(productImage, 'scale')) || 1;
        const bagTransformOrigin = gsap.getProperty(productImage, 'transformOrigin') || 'center center';
        
        // Get actual bag dimensions (accounting for current scale)
        const bagRect = productImage.getBoundingClientRect();
        const bagNaturalWidth = bagRect.width / bagScale;
        const bagNaturalHeight = bagRect.height / bagScale;
        
        // Calculate tag position as percentage of bag image dimensions
        // These percentages represent the position on the actual bag image (unscaled)
        // Position relative to top-left corner of the bag image
        // naturalOffset is relative to bag center, so we need to add half dimensions
        const tagPercentX = ((bagNaturalWidth / 2 + naturalOffsetX) / bagNaturalWidth) * 100;
        const tagPercentY = ((bagNaturalHeight / 2 + naturalOffsetY) / bagNaturalHeight) * 100;
        
        // Calculate bag's top-left corner position in GSAP space
        let bagTopLeftX, bagTopLeftY;
        
        if (isTablet) {
          // For tablets, transform-origin is 'center'
          const container = productImage.parentElement; // .image-preview
          if (container) {
            const containerRect = container.getBoundingClientRect();
            const bagCenterScreenX = bagRect.left + bagRect.width / 2;
            const bagCenterScreenY = bagRect.top + bagRect.height / 2;
            const containerCenterX = containerRect.left + containerRect.width / 2;
            const containerCenterY = containerRect.top + containerRect.height / 2;
            
            // Bag center in GSAP space
            const bagCenterX = bagCenterScreenX - containerCenterX;
            const bagCenterY = bagCenterScreenY - containerCenterY;
            
            // Top-left corner = center - half dimensions (scaled)
            bagTopLeftX = bagCenterX - (bagNaturalWidth * bagScale / 2);
            bagTopLeftY = bagCenterY - (bagNaturalHeight * bagScale / 2);
          } else {
            bagTopLeftX = bagX - (bagNaturalWidth * bagScale / 2);
            bagTopLeftY = bagY - (bagNaturalHeight * bagScale / 2);
          }
        } else if (bagTransformOrigin === 'left' || bagTransformOrigin.includes('left')) {
          // Bag's transform origin is left edge, so bagX/Y is the left edge
          bagTopLeftX = bagX;
          bagTopLeftY = bagY;
        } else {
          // Bag's transform origin is center
          bagTopLeftX = bagX - (bagNaturalWidth * bagScale / 2);
          bagTopLeftY = bagY - (bagNaturalHeight * bagScale / 2);
        }
        
        // Calculate tag position: top-left corner + percentage offset (scaled)
        const tagX = bagTopLeftX + (bagNaturalWidth * bagScale * tagPercentX / 100);
        const tagY = bagTopLeftY + (bagNaturalHeight * bagScale * tagPercentY / 100);
        
        // Update tag position (scale stays constant)
        gsap.set(tagElement, {
          x: tagX,
          y: tagY,
          scale: tagScale,
          rotation: 74,
          transformOrigin: 'center center',
          opacity: initialsCurrentOpacity,
          force3D: true
        });
        
        // Console log during animation for debugging
        if (Math.random() < 0.1) { // Log ~10% of updates to avoid spam
          console.log('[initials-tag] GSAP update:', {
            bagPosition: { x: bagX, y: bagY, scale: bagScale },
            bagTopLeft: { x: bagTopLeftX, y: bagTopLeftY },
            bagNaturalSize: { width: bagNaturalWidth, height: bagNaturalHeight },
            tagPercent: { x: tagPercentX.toFixed(2) + '%', y: tagPercentY.toFixed(2) + '%' },
            tagPosition: { x: tagX, y: tagY },
            naturalOffset: { x: naturalOffsetX, y: naturalOffsetY }
          });
        }
      };
      
      // Set initial position with constant scale
      gsap.set(tagElement, {
        scale: tagScale,
        rotation: 74,
        transformOrigin: 'center center',
        opacity: initialsCurrentOpacity,
        force3D: true
      });
      updateTagPosition();
      
      // Animate tag position by syncing with bag's animation in real-time
      // Tag scale stays constant at 1.3, only position updates
      tl.to(tagElement, {
        rotation: 74,
        duration: 1.5,
        ease: 'power2.inOut',
        force3D: true,
        onUpdate: updateTagPosition, // Sync position on every frame
        onComplete: () => {
          // Set final inline styles for tagElement (same as productImage)
          const finalTagX = parseFloat(gsap.getProperty(tagElement, 'x')) || 0;
          const finalTagY = parseFloat(gsap.getProperty(tagElement, 'y')) || 0;
          const finalTagScale = parseFloat(gsap.getProperty(tagElement, 'scale')) || tagScale;
          const finalTagRotation = parseFloat(gsap.getProperty(tagElement, 'rotation')) || 74;
          
          // Separate rules for each iPad model
          // Recalculate viewport width in case device was rotated
          const currentViewportWidth = window.innerWidth;
          const currentViewportHeight = window.innerHeight;
          const isPortrait = currentViewportHeight > currentViewportWidth;
          // Check iPad models with non-overlapping ranges to avoid conflicts
          const currentIsIpadMini = currentViewportWidth >= 768 && currentViewportWidth < 820;
          const currentIsIpadAir = currentViewportWidth >= 820 && currentViewportWidth < 1024;
          const currentIsIpadPro = currentViewportWidth >= 1024 && currentViewportWidth <= 1366;
          const currentIsTablet = currentViewportWidth >= 768 && currentViewportWidth <= 1366;
          const isIpadMiniLandscape = currentIsTablet && currentIsIpadMini && !isPortrait;
          const isIpadProLandscape = currentIsTablet && currentIsIpadPro && !isPortrait;
          const isIpadAirLandscape = currentIsTablet && currentIsIpadAir && !isPortrait;
          
          // Set inline styles with separate rules for each iPad model
          if (isIpadMiniLandscape) {
            // iPad Mini landscape - use calculated values
            tagElement.style.setProperty('transform', `translate3d(${finalTagX}px, ${finalTagY}px, 0px) scale(${finalTagScale}, ${finalTagScale}) rotate(${finalTagRotation}deg)`, 'important');
          } else if (isIpadAirLandscape) {
            // iPad Air landscape - specific transform values
            tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
          } else {
            // Other tablets and desktop - use calculated values
            tagElement.style.setProperty('transform', `translate3d(${finalTagX}px, ${finalTagY}px, 0px) scale(${finalTagScale}, ${finalTagScale}) rotate(${finalTagRotation}deg)`, 'important');
          }
          tagElement.style.setProperty('transform-origin', 'center center', 'important');
          
          // For tablets, set margin values for final positioning based on iPad model and orientation
          if (currentIsTablet) {
            // Reuse isPortrait from above
            
            if (isPortrait) {
              // Portrait orientation - different values for each iPad model
              if (currentIsIpadPro) {
                // iPad Pro portrait
                tagElement.style.setProperty('margin-top', '276px', 'important');
                tagElement.style.setProperty('margin-right', '-463px', 'important');
              } else if (currentIsIpadAir) {
                // iPad Air portrait
                tagElement.style.setProperty('margin-top', '166px', 'important');
                tagElement.style.setProperty('margin-right', '-338px', 'important');
                tagElement.style.setProperty('font-size', '22px', 'important');
              } else if (currentIsIpadMini) {
                // iPad Mini portrait
                tagElement.style.setProperty('margin-top', '72px', 'important');
                tagElement.style.setProperty('margin-right', '-233px', 'important');
                tagElement.style.setProperty('font-size', '19px', 'important');
              }
            } else {
              // Landscape orientation - different values for each iPad model
              // Use landscape-specific variables to avoid conflicts at boundary widths (e.g., 1024px)
              if (isIpadProLandscape) {
                // iPad Pro landscape - final positioning
                tagElement.style.setProperty('margin-top', '96px', 'important');
                tagElement.style.setProperty('margin-right', '-226px', 'important');
                tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
                tagElement.style.setProperty('font-size', '20px', 'important');
              } else if (isIpadAirLandscape) {
                // iPad Air landscape - final positioning
                tagElement.style.setProperty('margin-top', '96px', 'important');
                tagElement.style.setProperty('margin-right', '-226px', 'important');
                tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
                tagElement.style.setProperty('font-size', '20px', 'important');
              } else {
                // Other tablets landscape - use default values
                tagElement.style.setProperty('margin-top', '19px', 'important');
                tagElement.style.setProperty('margin-right', '-315px', 'important');
              }
            }
          } else {
            tagElement.style.setProperty('margin-top', '0px', 'important');
            tagElement.style.setProperty('margin-right', '0px', 'important');
          }
        }
      }, '<'); // Start at EXACT same time as bag animation
    }
  };


  useEffect(() => {
    // Always use products from JSON file, ignore localStorage
    // Clear localStorage to prevent old cached data
    try {
      localStorage.removeItem('valentino_products');
    } catch (e) {
      // Ignore errors
    }
    
    // Use products directly from JSON
    const processedProducts = productsJson.map(p => {
      const thumbImage = resolveGallery(p.image);
      const slideImage = findConfiguratorImage(p.image, p.slides);
      if (!thumbImage || thumbImage === '') {
        console.warn(`⚠️ Failed to resolve image for product ${p.id} (${p.name}):`, p.image);
      }
      return {
        ...p,
        image: thumbImage,
        configuratorImage: slideImage || thumbImage,
      };
    });
    
    // Log all processed products
    processedProducts.forEach((p, idx) => {
      //console.log(`${idx + 1}. ${p.name} (ID: ${p.id}) - Image: ${p.image ? '✓' : '✗'}`);
    });
    
    setProducts(processedProducts);
    // Update main image and selected index based on initialProductIdx
    const idxToUse = initialProductIdx !== null && initialProductIdx >= 0 && initialProductIdx < processedProducts.length 
      ? initialProductIdx 
      : 0;
    if (processedProducts.length > 0) {
      setMainImage(processedProducts[idxToUse]?.configuratorImage || processedProducts[idxToUse]?.image || '/bag.png');
      setSelectedProductIdx(idxToUse);
    }

    // Reset thumbnails slider to start (scroll to beginning)
    // Use multiple timeouts to ensure DOM is fully updated and rendered
    const resetSlider = () => {
      if (thumbnailsRef.current) {
        const slider = thumbnailsRef.current;
        // Temporarily disable smooth scrolling for instant reset
        const originalScrollBehavior = slider.style.scrollBehavior;
        slider.style.scrollBehavior = 'auto';
        
        // Reset scroll position
        slider.scrollLeft = 0;
        // Also try scrollTo for better browser compatibility
        slider.scrollTo({ left: 0, behavior: 'auto' });
        
        // Restore original scroll behavior after a short delay
        setTimeout(() => {
          slider.style.scrollBehavior = originalScrollBehavior || '';
        }, 50);
        
      }
    };
    
    // Try multiple times to ensure it works
    setTimeout(resetSlider, 0);
    setTimeout(resetSlider, 100);
    setTimeout(resetSlider, 300);
  }, []);

  // controls-panel reveal is handled by CSS animation: fadeIn (no GSAP inline styles)

  // virtual_page_load on BagBuilder mount
  useEffect(() => {
    const sku = products[selectedProductIdx] ? getProductSku(products[selectedProductIdx]) : '';
    updateDataLayer({ page_type: 'bag_builder', pagename: `/bag-builder/${sku}`, sku });
    trackPageView({ page_type: 'bag_builder', pagename: `/bag-builder/${sku}`, sku });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // virtual_page_load on final screen (stage 2)
  useEffect(() => {
    if (stage === 2) {
      const sku = products[selectedProductIdx] ? getProductSku(products[selectedProductIdx]) : '';
      updateDataLayer({ page_type: 'bag_builder_final', pagename: `/bag-builder/${sku}/final`, sku });
      trackPageView({ page_type: 'bag_builder_final', pagename: `/bag-builder/${sku}/final`, sku });
    }
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Show final content when stage changes to 2 (no animation, just show)
    if (stage === 2 && finalContentRef.current) {
      finalContentRef.current.style.visibility = 'visible';
      finalContentRef.current.style.transition = 'opacity 0.8s ease';
      finalContentRef.current.style.opacity = '1';
    }

    // Tag positioning handled by CSS
    if (false && stage === 2 && initialsTagRef.current) {
      const tagElement = initialsTagRef.current;
      const viewportWidth = window.innerWidth;
      const isTablet = viewportWidth >= 768 && viewportWidth <= 1366;
      
      // Check iPad models with non-overlapping ranges to avoid conflicts
      // iPad Mini: 768px - 820px (exclusive)
      const isIpadMini = viewportWidth >= 768 && viewportWidth < 820;
      // iPad Air: 820px - 1024px (exclusive)
      const isIpadAir = viewportWidth >= 820 && viewportWidth < 1024;
      // iPad Pro: 1024px - 1366px (inclusive)
      const isIpadPro = viewportWidth >= 1024 && viewportWidth <= 1366;
      
      // For iPad Pro only, use percentage-based positioning
      if (isTablet && isIpadPro) {
        // Check iPad Pro landscape specifically
        const viewportHeight = window.innerHeight;
        const isPortrait = viewportHeight > viewportWidth;
        const isIpadProLandscape = isTablet && isIpadPro && !isPortrait;
        
        // Ensure percentage-based positioning is maintained after animation
        // Use final position coordinates after animation completes
        tagElement.style.position = 'absolute';
        // iPad Pro - use default final positioning
        tagElement.style.left = 'calc(87.2% + 0px)';
        tagElement.style.top = 'calc(86.5% + 0px)';
        tagElement.style.transform = 'translate(-50%, -50%) rotate(74deg)';
        tagElement.style.zIndex = '2';
        tagElement.style.pointerEvents = 'none';
        tagElement.style.marginTop = '0';
        tagElement.style.marginRight = '0';
        return; // Exit early for iPad Pro only
      }
      
      // Get final GSAP values
      const finalTagX = parseFloat(gsap.getProperty(tagElement, 'x')) || 0;
      const finalTagY = parseFloat(gsap.getProperty(tagElement, 'y')) || 0;
      // Separate scale rules for each iPad model
      const viewportHeight = window.innerHeight;
      const isPortrait = viewportHeight > viewportWidth;
      const isIpadMiniLandscape = isIpadMini && !isPortrait;
      const isIpadProLandscape = isIpadPro && !isPortrait;
      const isIpadAirLandscape = isIpadAir && !isPortrait;
      
      // Separate scale values for each iPad model
      let finalTagScale;
      if (isIpadMiniLandscape) {
        finalTagScale = parseFloat(gsap.getProperty(tagElement, 'scale')) || 1.1;
      } else if (isIpadAirLandscape) {
        finalTagScale = parseFloat(gsap.getProperty(tagElement, 'scale')) || 0.9;
      } else if (isTablet) {
        finalTagScale = parseFloat(gsap.getProperty(tagElement, 'scale')) || 1.1;
      } else {
        finalTagScale = parseFloat(gsap.getProperty(tagElement, 'scale')) || 1.3;
      }
      
      const finalTagRotation = parseFloat(gsap.getProperty(tagElement, 'rotation')) || 74;
      
      // Set inline styles to ensure they're applied on final stage
      // Separate transform rules for each iPad model
      
      if (isIpadMiniLandscape) {
        // iPad Mini landscape - specific transform values
        tagElement.style.setProperty('transform', `translate3d(${finalTagX}px, ${finalTagY}px, 0px) scale(${finalTagScale}, ${finalTagScale}) rotate(${finalTagRotation}deg)`, 'important');
      } else if (isIpadProLandscape) {
        // iPad Pro landscape - specific transform values
        tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
      } else if (isIpadAirLandscape) {
        // iPad Air landscape - specific transform values
        tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
      } else {
        // Other tablets and desktop - use calculated values
        tagElement.style.setProperty('transform', `translate3d(${finalTagX}px, ${finalTagY}px, 0px) scale(${finalTagScale}, ${finalTagScale}) rotate(${finalTagRotation}deg)`, 'important');
      }
      tagElement.style.setProperty('transform-origin', 'center center', 'important');
      
      // For tablets, set margin values for final positioning based on iPad model and orientation
      if (isTablet) {
        // Reuse viewportHeight from above
        const isPortrait = viewportHeight > viewportWidth;
        
        if (isPortrait) {
          // Portrait orientation - different values for each iPad model
          if (isIpadPro) {
            // iPad Pro portrait
            tagElement.style.setProperty('margin-top', '276px', 'important');
            tagElement.style.setProperty('margin-right', '-463px', 'important');
          } else if (isIpadAir) {
            // iPad Air portrait - centered (no margins)
            tagElement.style.setProperty('margin-top', '0px', 'important');
            tagElement.style.setProperty('margin-right', '0px', 'important');
            tagElement.style.setProperty('font-size', '22px', 'important');
          } else if (isIpadMini) {
            // iPad Mini portrait - improved positioning
            tagElement.style.setProperty('margin-top', '45px', 'important');
            tagElement.style.setProperty('margin-right', '-210px', 'important');
            tagElement.style.setProperty('font-size', '19px', 'important');
          }
        } else {
          // Landscape orientation - different values for each iPad model
          // Use landscape-specific variables to avoid conflicts at boundary widths (e.g., 1024px)
          if (isIpadProLandscape) {
            // iPad Pro landscape - final positioning
            tagElement.style.setProperty('margin-top', '96px', 'important');
            tagElement.style.setProperty('margin-right', '-226px', 'important');
            tagElement.style.setProperty('transform', 'translate3d(161.596px, 331.1px, 0px) scale(0.9, 0.9) rotate(74deg)', 'important');
            tagElement.style.setProperty('font-size', '20px', 'important');
          } else if (isIpadAirLandscape) {
            // iPad Air landscape - centered (no margins)
            tagElement.style.setProperty('margin-top', '0px', 'important');
            tagElement.style.setProperty('margin-right', '0px', 'important');
            tagElement.style.setProperty('font-size', '20px', 'important');
          } else {
            // Other tablets landscape (iPad Mini landscape) - use default values
            tagElement.style.setProperty('margin-top', '19px', 'important');
            tagElement.style.setProperty('margin-right', '-315px', 'important');
          }
        }
      } else {
        tagElement.style.setProperty('margin-top', '0px', 'important');
        tagElement.style.setProperty('margin-right', '0px', 'important');
      }
    }
    
    // Animate scale to 1 for image-preview when stage becomes 2 (tablet and mobile)
    if (stage === 2 && imagePreviewRef.current) {
      const viewportWidth = window.innerWidth;
      const isTablet = viewportWidth >= 768 && viewportWidth <= 1366;
      const isMobile = viewportWidth <= 767;
      
      if (isTablet || isMobile) {
        // Kill any ongoing GSAP animations for image-preview
        gsap.killTweensOf(imagePreviewRef.current);

        // Ensure image is visible
        imagePreviewRef.current.style.setProperty('opacity', '1', 'important');
        imagePreviewRef.current.style.setProperty('visibility', 'visible', 'important');

        // For tablets, set scale immediately without animation
        if (isTablet) {
          imagePreviewRef.current.style.setProperty('transform-origin', '-60% 280%', 'important');
          const isLandscape = window.innerWidth > window.innerHeight;
          // Landscape tablets: bag stays in stage 1 position (split layout like Figma)
          if (!isLandscape && imagePreviewRef.current) {
            imagePreviewRef.current.style.setProperty('transform', 'scale(1.1)', 'important');
          }
        } else {
          // Mobile: clear inline transforms so CSS rules take over static positioning
          imagePreviewRef.current.style.removeProperty('transform');
          imagePreviewRef.current.style.removeProperty('transform-origin');
        }
      }
    }
    
    // Set final styles for product-image on stage 2 - separate rules for each iPad model
    if (stage === 2 && productImageRef.current) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isTablet = viewportWidth >= 768 && viewportWidth <= 1366;
      // Check iPad models with non-overlapping ranges to avoid conflicts
      // iPad Mini: 768px - 820px (exclusive)
      const isIpadMini = viewportWidth >= 768 && viewportWidth < 820;
      // iPad Air: 820px - 1024px (exclusive)
      const isIpadAir = viewportWidth >= 820 && viewportWidth < 1024;
      // iPad Pro: 1024px - 1366px (inclusive)
      const isIpadPro = viewportWidth >= 1024 && viewportWidth <= 1366;
      const isPortrait = viewportHeight > viewportWidth;
      const isIpadMiniLandscape = isTablet && isIpadMini && !isPortrait;
      const isIpadAirLandscape = isTablet && isIpadAir && !isPortrait;
      const isIpadProLandscape = isTablet && isIpadPro && !isPortrait;
      
      if (isTablet) {
        const isLandscape = window.innerWidth > window.innerHeight;

        // Landscape tablets: bag stays in stage 1 position (split layout)
        if (!isLandscape) {
          productImageRef.current.style.setProperty('transform', 'translate3d(30px, -10px, 0px) scale(0.8, 0.8)', 'important');
          productImageRef.current.style.setProperty('transform-origin', 'center center', 'important');
        }
      }
    }
  }, [stage]);

  // Scale image-preview for tablet and mobile versions when configurator is loaded
  useEffect(() => {
    console.log('[BagBuilder] Scale animation effect triggered, stage:', stage);
    
    if (stage === 1) {
      const getViewportInfo = () => {
        const width = window.innerWidth;
        return {
          width,
          isTablet: width >= 768 && width <= 1366,
          isMobile: width <= 767
        };
      };
      
      const viewportInfo = getViewportInfo();
      const { width: viewportWidth, isTablet, isMobile } = viewportInfo;
      
      console.log('[BagBuilder] Viewport width:', viewportWidth, 'isTablet:', isTablet, 'isMobile:', isMobile);
      
      if (isTablet || isMobile) {
        let timer = null;
        let rafId2 = null;
        let animationInstance = null;
        
        // Kill any existing animations first
        if (imagePreviewRef.current) {
          gsap.killTweensOf(imagePreviewRef.current);
        }
        
        // Use requestAnimationFrame to ensure DOM is ready
        const rafId1 = requestAnimationFrame(() => {
          rafId2 = requestAnimationFrame(() => {
            console.log('[BagBuilder] RAF callback, imagePreviewRef.current:', !!imagePreviewRef.current, 'stage:', stage);
            
            if (imagePreviewRef.current && stage === 1) {
              // Get current viewport info to ensure we use correct values
              const currentViewportInfo = getViewportInfo();
              const currentIsMobile = currentViewportInfo.isMobile;
              const currentIsTablet = currentViewportInfo.isTablet;
              
              // For tablets and mobile: CSS controls transform, don't set inline styles
              if (currentIsTablet || currentIsMobile) {
                // Skip — CSS handles transform
              } else {
                imagePreviewRef.current.style.setProperty('transform-origin', '65% 100%', 'important');
                imagePreviewRef.current.style.setProperty('transform', 'scale(1)', 'important');
              }
              
              console.log('[BagBuilder] Setting timer for 3 seconds...');
              
              // Wait 3 seconds after component is loaded, then scale
              timer = setTimeout(() => {
                console.log('[BagBuilder] Timer fired, imagePreviewRef.current:', !!imagePreviewRef.current, 'stage:', stage);
                
                if (imagePreviewRef.current && stage === 1) {
                  // Re-check viewport info at animation start to handle device switching
                  const finalViewportInfo = getViewportInfo();
                  const finalIsMobile = finalViewportInfo.isMobile;
                  const finalIsTablet = finalViewportInfo.isTablet;
                  
                  console.log('[BagBuilder] Starting scale animation');
                  
                  // Kill any animations that might have started
                  gsap.killTweensOf(imagePreviewRef.current);
                  
                  // Force reflow to ensure element is ready
                  void imagePreviewRef.current.offsetHeight;
                  
                  // For tablets and mobile: CSS controls transform — skip inline style
                  if (!finalIsTablet && !finalIsMobile) {
                    imagePreviewRef.current.style.setProperty('transform-origin', '65% 100%', 'important');
                    imagePreviewRef.current.style.setProperty('transform', 'scale(1)', 'important');
                  }

                  // For tablets and mobile: CSS controls transform — skip inline style
                  if (!finalIsTablet && !finalIsMobile) {
                    // Determine target scale: Desktop: 2
                    const targetScale = 2;
                    
                    console.log('[BagBuilder] Starting animation: scale to', targetScale);
                    
                    // Create a proxy object to animate scale value
                    const scaleProxy = { value: 1 };
                    
                    // Single animation: smoothly scale to target value
                    animationInstance = gsap.to(scaleProxy, {
                      value: targetScale,
                      duration: 2,
                      ease: 'power2.out',
                      onUpdate: function() {
                        if (imagePreviewRef.current && stage === 1) {
                          imagePreviewRef.current.style.setProperty('transform', `scale(${scaleProxy.value})`, 'important');
                        }
                      },
                      onStart: () => {
                        console.log('[BagBuilder] Animation started');
                      },
                      onComplete: () => {
                        console.log('[BagBuilder] Animation completed');
                      }
                    });
                  }
                }
              }, 3000); // 3 seconds delay
            } else {
              console.warn('[BagBuilder] RAF check failed: imagePreviewRef.current:', !!imagePreviewRef.current, 'stage:', stage);
            }
          });
        });
        
        return () => {
          console.log('[BagBuilder] Cleanup: cancelling animation');
          cancelAnimationFrame(rafId1);
          if (rafId2) cancelAnimationFrame(rafId2);
          if (timer) clearTimeout(timer);
          if (animationInstance) {
            animationInstance.kill();
          }
          if (imagePreviewRef.current) {
            gsap.killTweensOf(imagePreviewRef.current);
          }
        };
      } else {
        console.log('[BagBuilder] Not a tablet or mobile device, skipping animation');
      }
    } else {
      console.log('[BagBuilder] Stage is not 1, current stage:', stage);
    }
  }, [stage]);


  // Reset thumbnails slider to start when products change
  // Remove overflow hidden for body on mobile when bag-builder-wrapper is active
  useEffect(() => {
    const checkAndUpdateOverflow = () => {
      const isMobile = window.innerWidth <= 767;
      
      if (isMobile) {
        // Remove overflow hidden for mobile
        document.body.style.overflow = '';
      }
    };
    
    // Check on mount
    checkAndUpdateOverflow();
    
    // Also check on window resize
    window.addEventListener('resize', checkAndUpdateOverflow);
    
    // Cleanup: restore original overflow when component unmounts
    return () => {
      window.removeEventListener('resize', checkAndUpdateOverflow);
      const isMobile = window.innerWidth <= 767;
      if (isMobile) {
        document.body.style.overflow = '';
      }
    };
  }, []);

  useEffect(() => {
    if (thumbnailsRef.current && products.length > 0) {
      // Reset scroll position to start - use multiple attempts to ensure it works
      const resetSlider = () => {
        if (thumbnailsRef.current) {
          const slider = thumbnailsRef.current;
          // Temporarily disable smooth scrolling for instant reset
          const originalScrollBehavior = slider.style.scrollBehavior;
          slider.style.scrollBehavior = 'auto';
          
          // Reset scroll position
          slider.scrollLeft = 0;
          // Also try scrollTo for better browser compatibility
          slider.scrollTo({ left: 0, behavior: 'auto' });
          
          // Restore original scroll behavior after a short delay
          setTimeout(() => {
            slider.style.scrollBehavior = originalScrollBehavior || '';
          }, 50);
        }
      };
      
      // Immediate reset
      resetSlider();
      
      // Also reset after delays to handle any async rendering
      setTimeout(resetSlider, 0);
      setTimeout(resetSlider, 100);
      setTimeout(resetSlider, 300);
    }
  }, [products]);

  // Touch drag scroll for thumbnails on tablet (iOS Safari workaround)
  useEffect(() => {
    const el = thumbnailsRef.current;
    if (!el) return;

    let startX = 0;
    let scrollLeftStart = 0;
    let isDragging = false;

    const onTouchStart = (e) => {
      isDragging = true;
      startX = e.touches[0].clientX;
      scrollLeftStart = el.scrollLeft;
      el.style.scrollBehavior = 'auto';
    };

    const onTouchMove = (e) => {
      if (!isDragging) return;
      const dx = e.touches[0].clientX - startX;
      if (Math.abs(dx) > 5) {
        thumbnailScrollingRef.current = true;
      }
      el.scrollLeft = scrollLeftStart - dx;
    };

    const onTouchEnd = () => {
      isDragging = false;
      el.style.scrollBehavior = '';
      setTimeout(() => {
        thumbnailScrollingRef.current = false;
      }, 50);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  // Register scroll event listener for parallax effect on mobile and tablet
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const viewportWidth = window.innerWidth;
    const isMobileDevice = viewportWidth <= 767;
    const isTabletDevice = viewportWidth >= 768 && viewportWidth <= 1366;
    
    if (!wrapper || (!isMobileDevice && !isTabletDevice) || stage !== 1) return;

    wrapper.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial call to set correct position
    handleScroll();

    // Recalculate when content changes (products loaded, etc.)
    const recalculateTimeout = setTimeout(() => {
      handleScroll();
    }, 100);

    return () => {
      wrapper.removeEventListener('scroll', handleScroll);
      clearTimeout(recalculateTimeout);
    };
  }, [isMobile, isTablet, stage, handleScroll, products.length]);

  // Determine if parallax should be applied (mobile or tablet)
  const shouldApplyParallax = useMemo(() => {
    const viewportWidth = window.innerWidth;
    return viewportWidth <= 767 || (viewportWidth >= 768 && viewportWidth <= 1366);
  }, [isMobile]);

  // Apply inline styles for tablets using useEffect with setProperty for !important
  // Use interval to continuously enforce styles and override any other operations
  useEffect(() => {
    if (!imagePreviewRef.current) return;
    
    const applyStyles = () => {
      if (imagePreviewRef.current && isTablet && stage === 1) {
        // Clear any previously set inline transforms so CSS media queries take full control
        imagePreviewRef.current.style.removeProperty('transform');
        imagePreviewRef.current.style.removeProperty('transform-origin');
      }
    };
    
    // Apply immediately
    applyStyles();
    
    // Apply after a delay to override other operations
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(applyStyles);
    }, 100);
    
    // Use interval to continuously enforce styles (every 200ms)
    const intervalId = setInterval(() => {
      if (isTablet && stage === 1) {
        applyStyles();
      }
    }, 200);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [isTablet, stage]);

  // Apply inline styles for tablets in stage 2 (iPad Air and iPad Mini)
  useEffect(() => {
    if (!initialsTagRef.current) return;
    
    const applyStage2Styles = () => {
      if (initialsTagRef.current && isTablet && stage === 2) {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isPortrait = viewportHeight > viewportWidth;
        
        // Check iPad models
        const isIpadMini = viewportWidth >= 768 && viewportWidth < 820;
        const isIpadAir = viewportWidth >= 820 && viewportWidth < 1024;
        const isIpadPro = viewportWidth >= 1024 && viewportWidth <= 1366;
        const isIpadAirLandscape = isIpadAir && !isPortrait;
        
        // Skip iPad Pro - it uses percentage-based positioning
        if (isIpadPro) return;
        
        const tagElement = initialsTagRef.current;
        
        if (isIpadAir) {
          // iPad Air - centered (no margins) for both portrait and landscape
          tagElement.style.setProperty('margin-top', '0px', 'important');
          tagElement.style.setProperty('margin-right', '0px', 'important');
        } else if (isIpadMini && isPortrait) {
          // iPad Mini portrait - improved positioning
          tagElement.style.setProperty('margin-top', '45px', 'important');
          tagElement.style.setProperty('margin-right', '-210px', 'important');
        } else if (isIpadMini && !isPortrait) {
          // iPad Mini landscape - keep existing values
          tagElement.style.setProperty('margin-top', '19px', 'important');
          tagElement.style.setProperty('margin-right', '-315px', 'important');
        }
      }
    };
    
    // Apply immediately
    applyStage2Styles();
    
    // Apply after a delay to override other operations
    const timeoutId = setTimeout(() => {
      requestAnimationFrame(applyStage2Styles);
    }, 100);
    
    // Use interval to continuously enforce styles (every 200ms)
    const intervalId = setInterval(() => {
      if (isTablet && stage === 2) {
        applyStage2Styles();
      }
    }, 200);
    
    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [isTablet, stage]);

  return (
    <div className="bag-builder-wrapper" ref={wrapperRef}>
      <div className="main-container">
        <div 
          className={`image-preview ${stage === 2 ? 'image-preview--final' : ''}`} 
          ref={imagePreviewRef}
        >
          <img ref={productImageRef} src={mainImage} alt="Floral handbag" className="product-image" />
          <div id="initials-tag" ref={initialsTagRef} className="initials-tag" style={{ backgroundImage: `url(${[11, 27].includes(products[selectedProductIdx]?.id) ? brownleafImage : goldleafImage})`, right: selectedProductIdx < 20 ? '413px' : undefined, filter: [11, 27].includes(products[selectedProductIdx]?.id) ? 'blur(.3px) brightness(155%)' : undefined }}>{displayInitials}</div>
        </div>

        {stage === 1 && (
          <div 
            className="controls-panel" 
            ref={controlsPanelRef}
            style={isMobile && shouldApplyParallax && stage === 1 ? { transform: `translateY(${panelDragY}px)` } : {}}
          >
            {(isMobile || isTablet) && (
              <div 
                className="home-indicator" 
                ref={homeIndicatorRef}
              >
                <div className="home-indicator__bar"></div>
              </div>
            )}
            <h2 className="title">{t('BAG_BUILDER_TITLE')}</h2>
            <div className="initials-section">
              <h3 className="section-title">{t('HOW_DO_YOU_PREFER_CHARACTERS')}</h3>
              <div className="initials-row">
                <div className="initials-options">
                  <button id="btn-no-dots" className={`option-btn ${format === 'no-dots' ? 'active' : ''}`} onClick={() => {
                    trackClick('bag_builder', 'format_no_dots');
                    setFormat('no-dots');
                  }}>{noDots}</button>
                  <button id="btn-with-dots" className={`option-btn ${format === 'with-dots' ? 'active' : ''}`} onClick={() => {
                    trackClick('bag_builder', 'format_with_dots');
                    setFormat('with-dots');
                  }}>{withDots}</button>
                </div>
                <a href="#edit" id="edit-initials-link" className="edit-initials" onClick={(e) => { 
                  e.preventDefault(); 
                  trackModalView('edit-initials');
                  setShowEditModal(true); 
                }}>{t('EDIT_CHARACTERS')}</a>
              </div>
            </div>

            <div className="models-section">
              <h3 className="section-title">{t('WOULD_YOU_LIKE_TO_SEE_OTHER_MODELS')}</h3>
              <div className="model-thumbnails-wrapper">
                <button className="thumbnail-nav thumbnail-nav--prev" onClick={() => scrollThumbnails('left')} aria-label="Previous">‹</button>
                <div className="model-thumbnails" ref={thumbnailsRef}>
                  {products.map((product, idx) => (
                    <label className={`thumbnail ${selectedProductIdx === idx ? 'thumbnail--selected' : ''}`} key={product.id} onClick={() => handleThumb(idx)} onTouchEnd={(e) => { e.preventDefault(); if (!thumbnailScrollingRef.current) { handleThumb(idx); } }}>
                      <img src={product.image} alt={product.name} />
                      <input type="radio" name="model" checked={selectedProductIdx === idx} readOnly />
                    </label>
                  ))}
                </div>
                <button className="thumbnail-nav thumbnail-nav--next" onClick={() => scrollThumbnails('right')} aria-label="Next">›</button>
              </div>
              <div className="action-buttons">
                <button className="confirm-btn" onClick={handleFirstConfirm}>
                  <span className="confirm-btn__text">{t('THATS_MY_DEVAIN')}</span>
                </button>
              </div>
              <p className="bag-builder-footer__text">{t('IMAGE_ILLUSTRATIVE_PURPOSES')}</p>
            </div>

          </div>
        )}

        {stage === 2 && (
          <>
            <div className="final-screen" ref={finalContentRef}>
              <h2 className="final-title">{t('BEHOLD_YOUR_UNIQUE_DEVAIN')}</h2>
              <p className="final-subtitle">{t('WHATS_NEXT')}</p>
              <div className="final-buttons">
                <a
                  href="https://myv-experience.valentino.com/boutiques?utm_source=experience&utm_medium=CTA&utm_campaign=DeVain"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="final-btn final-btn--primary"
                  onClick={() => trackClosingCTA('explore_boutique_catalogue')}
                >
                  {t('EXPLORE_BOUTIQUE_CATALOGUE')}
                </a>
                <button
                  className="final-btn final-btn--secondary"
                  onClick={() => {
                    trackClosingCTA('restart_the_experience');
                    window.location.href = '/';
                  }}
                >
                  {t('RESTART_THE_EXPERIENCE')}
                </button>
              </div>
            </div>
          </>
        )}

      </div>
      <div className={`modal modal--initials ${showEditModal ? 'is-open' : ''}`} id="initials-modal" aria-hidden={!showEditModal}>
        <div className="modal-top-bar">
          <div className="modal-title">{t('EDIT_YOUR_INITIALS')}</div>
          <button
            className="lang-close-model"
            type="button"
            aria-label="Close"
            onClick={() => {
              trackClick('bag_builder', 'close_edit_modal');
              setShowEditModal(false);
              trackBagBuilderPageView();
            }}>
            ×
          </button>
        </div>
        <div className="initials-modal-content">
          <p className="initials-modal-instruction">{t('ADD_UP_TO_THREE_CHARACTERS')}</p>
          <div className="initials-input-group">
            <input 
              type="text" 
              id="new-label-input" 
              className="initials-input"
              placeholder={t('ENTER_NEW_LABEL')} 
              maxLength={3} 
              defaultValue={noDots} 
              onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[^A-Za-z]/g, ''); }} onKeyDown={(e)=>{ if(e.key==='Enter'){ handleConfirmNewLabel(e.currentTarget.value); } }} 
            />
            <button 
              id="confirm-new-label" 
              className="initials-submit-btn"
              onClick={(e)=>{ 
                const input = document.getElementById('new-label-input');
                handleConfirmNewLabel(input?.value || ''); 
              }}>
              <img src={arrowIcon} alt="Submit" />
            </button>
          </div>
        </div>
      </div>
      <div className={`overflow-bg ${showEditModal ? 'is-open' : ''}`} onClick={() => {
        trackClick('bag_builder', 'close_edit_modal_overlay');
        setShowEditModal(false);
        trackBagBuilderPageView();
      }}></div>
      <div className="bag-builder-footer">
        <p className="bag-builder-footer__text">{t('IMAGE_ILLUSTRATIVE_PURPOSES')}</p>
      </div>
    </div>
  );
};

export default BagBuilder;

